import { useContext, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, CircleSlash } from 'lucide-react'
import { NOT_ENTITLED, SessionExpired, get } from '../../components/cases/api'
import { AuthContext } from '../../context/AuthContext'
import { useResource } from '../../components/cases/useResource'
import { Button, Empty, Panel, Refusal, Spinner } from '../../components/cases/ui'
import Shell from '../../components/cases/Shell'
import CaseHeader from '../../components/cases/sections/CaseHeader'
import CaseActions from '../../components/cases/sections/CaseActions'
import Allegations from '../../components/cases/sections/Allegations'
import Evidence, { DisclosedToMe } from '../../components/cases/sections/Evidence'
import Meetings from '../../components/cases/sections/Meetings'
import WorkingNotes from '../../components/cases/sections/WorkingNotes'
import Parties from '../../components/cases/sections/Parties'
import Findings from '../../components/cases/sections/Findings'
import Disclosures from '../../components/cases/sections/Disclosures'
import Report from '../../components/cases/sections/Report'
import MyNotes from '../../components/cases/sections/MyNotes'
import { Audit, Transitions } from '../../components/cases/sections/History'

// ======================================================================
// THE CASE WORKSPACE
// ======================================================================
//
// ======================================================================
// THE 404 RULE GOVERNS THIS WHOLE SCREEN
// ======================================================================
//
// A 404 from any sub-resource means the caller is not entitled to it,
// and the render is NOTHING. Not an error. Not an empty card with a
// padlock. Not a tab that is present but greyed out. Not a tooltip
// explaining what they would need.
//
// er_cases/acl.py made every refusal a byte-identical 404 — case absent,
// wrong tenant, no party row, party revoked, capability absent — so that
// a caller cannot sort real case ids from noise, and so that the
// EXISTENCE of an ER case about a named person stays private. That is
// the sensitive fact, often more sensitive than the contents. A UI that
// renders "Evidence — you do not have permission" hands the whole thing
// back: the subject now knows there is evidence, how much of the case
// they are not being shown, and that the shape of the case is bigger
// than what they can see.
//
// So sections do not exist unless their data exists. `useResource`
// turns a 404 into `entitled: false` and this component renders null.
//
// ======================================================================
// WHY CAPABILITIES DECIDE WHICH ENDPOINTS ARE CALLED
// ======================================================================
//
// CaseDetail echoes the caller's own `capabilities`, resolved by the
// same ACL dependency that authorised the request, "so the UI can decide
// which tabs to render without a second round trip".
//
// Using it is not an authorisation decision — require_case still runs on
// every endpoint and is the only thing that matters. It is about the
// AUDIT TRAIL. Every refused read writes a durable access_denied row to
// the case's hash chain, out of band, so it survives even when the
// request rolls back. A UI that fired all seventeen endpoints at every
// case and let the 404s sort it out would fill the SUBJECT'S OWN audit
// trail with a dozen denials the subject never caused, every time they
// opened their case — noise in the one record that is supposed to be
// legible, and a false impression that somebody was probing.

export default function CaseWorkspace() {
  const { caseId } = useParams()
  // The caller's own user id, for UI hints only — "is this my working
  // note", "did I record this finding". Every one of those is enforced
  // server-side in a WHERE clause; it is never an authorisation
  // decision here and is never sent anywhere.
  //
  // The standalone app decoded it from its own JWT's `sub` claim,
  // because it held the token and nothing else. Inside Kwery the user
  // object is already resolved on AuthContext, so the token is left
  // alone.
  const { user } = useContext(AuthContext)
  const me = user?.id ?? null

  const detail = useResource(`/${caseId}`)

  if (detail.loading) {
    return (
      <Shell>
        <div className="w-full px-5 py-8 sm:px-8">
          <Panel>
            <Spinner label="Opening the case" />
          </Panel>
        </div>
      </Shell>
    )
  }

  if (detail.error) {
    return (
      <Shell>
        <div className="w-full px-5 py-8 sm:px-8">
          <BackLink />
          <Refusal error={detail.error} />
          <Button onClick={() => detail.reload()}>Try again</Button>
        </div>
      </Shell>
    )
  }

  // NOT ENTITLED TO THE CASE HEADER. That is a witness or a companion —
  // they hold view_own_notes and nothing else. Their entire surface is
  // their own interview notes, so that is what this becomes. It is NOT
  // a fallback for an error, and it says nothing about what else the
  // case might contain.
  if (!detail.entitled) {
    return <IntervieweeOnly caseId={caseId} />
  }

  return <Workspace caseId={caseId} detail={detail} me={me} />
}

function Workspace({ caseId, detail, me }) {
  const d = detail.data
  const caps = d.capabilities

  const can = (c) => caps.includes(c)

  const allegations = useResource(`/${caseId}/allegations`, {
    enabled: can('view_allegations'),
  })
  const evidence = useResource(`/${caseId}/evidence`, { enabled: can('view_evidence') })
  const disclosedToMe = useResource(`/${caseId}/evidence/disclosed-to-me`, {
    enabled: can('view_case'),
  })
  const parties = useResource(`/${caseId}/parties`, { enabled: can('view_all_notes') })
  const meetings = useResource(`/${caseId}/meetings`, { enabled: can('view_all_notes') })
  const workingNotes = useResource(`/${caseId}/notes`, {
    enabled: can('add_working_note'),
  })
  const myNotes = useResource(`/${caseId}/my-notes`, { enabled: can('view_own_notes') })
  const findings = useResource(`/${caseId}/findings`, { enabled: can('view_case') })
  const disclosures = useResource(`/${caseId}/disclosures`, {
    enabled: can('view_evidence'),
  })
  const transitions = useResource(`/${caseId}/transitions?limit=50&offset=0`, {
    enabled: can('view_case'),
  })
  // ONE AUDIT ENDPOINT, NOT BOTH. view_case_audit opens the whole trail;
  // view_own_audit opens the caller's own rows. Asking for /audit
  // without the first would be a refusal written to the chain for no
  // gain, so the broader one is used when it is held and the narrower
  // one otherwise.
  const auditScope = can('view_case_audit') ? 'all' : can('view_own_audit') ? 'mine' : null
  const audit = useResource(
    auditScope === 'mine'
      ? `/${caseId}/audit/mine?limit=50&offset=0`
      : `/${caseId}/audit?limit=50&offset=0`,
    { enabled: auditScope != null },
  )

  const section = (resource, render, { showWhenEmpty = true } = {}) => {
    if (!resource.entitled || resource.error) return null
    if (resource.loading) return null
    const data = resource.data
    if (data == null) return null
    if (Array.isArray(data) && data.length === 0 && !showWhenEmpty) return null
    return render(data)
  }

  return (
    <Shell>
      {/* FULL WIDTH (task 26). See MyCases for why. The panels stack
          in one column as before — nothing is rearranged — and the prose
          inside each one carries .measure so an allegation does not run
          the whole width of a 4K monitor. */}
      <div className="w-full space-y-4 px-5 py-8 sm:px-8">
        <BackLink />

        <CaseHeader detail={d} />

        {can('edit_case_details') && (
          <div className="px-1">
            <CaseActions
              caseId={caseId}
              detail={d}
              onUpdated={(next) => detail.replace(next)}
            />
          </div>
        )}

        {section(allegations, (items) => (
          <Allegations
            caseId={caseId}
            items={items}
            capabilities={caps}
            parties={parties.entitled ? parties.data : null}
            onReplace={allegations.replace}
          />
        ))}

        {section(evidence, (items) => (
          <Evidence
            caseId={caseId}
            items={items}
            allegations={allegations.entitled ? allegations.data : null}
            capabilities={caps}
            parties={parties.entitled ? parties.data : null}
            onReplace={evidence.replace}
          />
        ))}

        {/* Only for somebody who is actually a recipient. A case owner
            holds view_evidence and sees the exhibit list; an empty "what
            you have been sent" panel above it would be noise. */}
        {section(
          disclosedToMe,
          (items) => <DisclosedToMe caseId={caseId} items={items} />,
          { showWhenEmpty: !can('view_evidence') },
        )}

        {section(myNotes, (items) => <MyNotes caseId={caseId} meetings={items} />, {
          showWhenEmpty: false,
        })}

        {section(meetings, (items) => (
          <Meetings
            caseId={caseId}
            items={items}
            capabilities={caps}
            parties={parties.entitled ? parties.data : null}
            onReplace={meetings.replace}
          />
        ))}

        {section(workingNotes, (items) => (
          <WorkingNotes
            caseId={caseId}
            items={items}
            me={me}
            onReplace={workingNotes.replace}
          />
        ))}

        {section(
          findings,
          (items) => (
            <Findings
              caseId={caseId}
              items={items}
              allegations={allegations.entitled ? allegations.data : null}
              capabilities={caps}
              caseStatus={d.status}
              me={me}
              onReplace={findings.replace}
            />
          ),
          { showWhenEmpty: can('record_finding') },
        )}

        {section(disclosures, (items) => <Disclosures items={items} />)}

        {section(parties, (items) => (
          <Parties
            caseId={caseId}
            items={items}
            capabilities={caps}
            me={me}
            onReplace={parties.replace}
          />
        ))}

        {section(transitions, (page) => <Transitions caseId={caseId} page={page} />)}

        {section(audit, (page) => (
          <Audit caseId={caseId} page={page} scope={auditScope} />
        ))}

        {can('generate_report') && (
          <Report caseId={caseId} canExportBundle={can('export_bundle')} />
        )}
      </div>
    </Shell>
  )
}

// ======================================================================
// STEP 5 — THE INTERVIEWEE SURFACE
// ======================================================================
//
// SEPARATE AND MINIMAL, for somebody whose only capability is
// view_own_notes. No case header, no stage rail, no navigation into the
// case, no other sections and no hint that any exist. They cannot open
// the case, and this screen does not imply there is one to open.
//
// It is reached by data, not by a role check: the case header came back
// 404, so there is no case surface for this caller, and /my-notes is
// what remains.

function IntervieweeOnly({ caseId }) {
  const [meetings, setMeetings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let live = true
    get(`/${caseId}/my-notes`)
      .then((res) => {
        if (!live) return
        setMeetings(res === NOT_ENTITLED ? null : res)
      })
      .catch((e) => {
        if (!live || e instanceof SessionExpired) return
        setError(e)
      })
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [caseId])

  return (
    <Shell>
      {/* FULL WIDTH LIKE THE REST, AND THE ONE THAT DESERVED THE MOST
          THOUGHT (task 26). This surface is a witness or a companion
          reading their own interview record and saying whether it is
          right — it is prose end to end, and full-bleed prose at 2560px
          would be worse than the narrow column it replaces.

          It is widened anyway, because a page that is 640px wide while
          every other page in the application is not reads as broken
          rather than as considered, and because the fix belongs on the
          TEXT and not on the page: MyNotes sets .measure on every
          paragraph it renders, so the reading width is preserved at any
          window size while the panels, headers and buttons line up with
          the rest of the module. */}
      <div className="w-full space-y-4 px-5 py-8 sm:px-8">
        <BackLink />

        <header className="px-1">
          <h1 className="font-head text-[24px] font-bold tracking-tight">
            Your interview
          </h1>
          <p className="mt-1 text-[13px] leading-relaxed text-muted">
            The record of what you said, and your chance to say whether it is
            right.
          </p>
        </header>

        <Refusal error={error} onDismiss={() => setError(null)} />

        {loading ? (
          <Panel>
            <Spinner />
          </Panel>
        ) : meetings ? (
          <MyNotes caseId={caseId} meetings={meetings} standalone />
        ) : (
          // Neither the case nor any notes. Stated as an absence and
          // nothing more — no cause, no suggestion that something exists
          // and is being withheld.
          <Panel>
            <Empty icon={CircleSlash} title="There is nothing here for you">
              If you were expecting something, the person who contacted you can
              tell you where to find it.
            </Empty>
          </Panel>
        )}
      </div>
    </Shell>
  )
}

function BackLink() {
  return (
    <Link
      to="/cases"
      className="btn btn-ghost btn-sm -ml-2 inline-flex w-auto"
    >
      <ArrowLeft size={13} strokeWidth={2} /> Your cases
    </Link>
  )
}
